import React, { useState, useEffect, useContext } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  InputNumber,
  Switch,
  Card,
  Typography,
  Layout,
  message,
  Row,
  Col,
  Divider,
} from "antd";
import { SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  BASE_URL,
  couponTypeOptions,
  discountTypeOptions,
} from "../../Constants";
import AppContext from "../../AppContext";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const { Content } = Layout;

function CouponForm() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [couponType, setCouponType] = useState("Coupon");
  const [discountType, setDiscountType] = useState("Percentage");
  const [plans, setPlans] = useState([]);
  const [eventPackages, setEventPackages] = useState([]);
  const [couponEventPackageSkus, setCouponEventPackageSkus] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const { profile } = useContext(AppContext);
  const isEdit = Boolean(id);
  const applicableProducts = Form.useWatch("applicableProducts", form) || [];
  const showPlanSelector = applicableProducts.includes("digital-card");
  const showWeddingPackageSelector = applicableProducts.includes("wedding");
  const showEventPackageSelector = applicableProducts.includes("event");
  // Wedding/Event trial coupons never use a day-based duration — the trial simply makes the
  // customer's selected package free, so the package (via the SKU selectors above) is what
  // determines everything the customer receives. This must stay in sync with the backend rule
  // in CouponService.ValidateCreateCouponAsync.
  const scopesEventProduct = showWeddingPackageSelector || showEventPackageSelector;
  const weddingPackages = eventPackages.filter((pkg) => pkg.productType === "wedding");
  const eventOnlyPackages = eventPackages.filter((pkg) => pkg.productType === "event");

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/Lookup/plans`);
      setPlans(response.data || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchEventPackages = async () => {
    try {
      // SuperAdmin-only listing so inactive packages (kept selectable/visible on existing
      // coupons but not newly selectable) are included alongside active ones.
      const response = await axios.get(`${BASE_URL}/EventProductPackage`);
      setEventPackages(response.data || []);
    } catch (error) {
      console.error("Error fetching event/wedding packages:", error);
    }
  };

  const fetchCoupon = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/Coupon/${id}`);

      if (response.status === 200) {
        const coupon = response.data;
        const selectedType = couponTypeOptions.find((option) => option.value.toLowerCase() === coupon.type.toLowerCase());
        const selectedDiscountType = discountTypeOptions.find((option) => option.value.toLowerCase() === coupon.discountType?.toLowerCase());
        setCouponType(selectedType?.value);
        if (coupon.discountType) {
          setDiscountType(selectedDiscountType?.value);
        }
        form.setFieldsValue({
          ...coupon,
          type: selectedType?.value || "Coupon",
          discountType: selectedDiscountType?.value || "Percentage",
          expirationDate: dayjs(coupon.expirationDate),
        });
        // Split the stored combined SKU list into the two selectors once the package
        // catalog (needed to know which SKU belongs to which product type) has loaded.
        setCouponEventPackageSkus(coupon.applicableEventPackageSkus || []);
      }
    } catch (error) {
      console.error("Error fetching coupon:", error);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchEventPackages();
    if (isEdit) {
      fetchCoupon();
    }
  }, []);

  useEffect(() => {
    if (couponEventPackageSkus === null || eventPackages.length === 0) return;
    const skuSet = new Set(couponEventPackageSkus.map((s) => s.toLowerCase()));
    form.setFieldsValue({
      weddingPackageSkus: weddingPackages
        .filter((pkg) => skuSet.has(pkg.sku.toLowerCase()))
        .map((pkg) => pkg.sku),
      eventPackageSkus: eventOnlyPackages
        .filter((pkg) => skuSet.has(pkg.sku.toLowerCase()))
        .map((pkg) => pkg.sku),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponEventPackageSkus, eventPackages]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = {
        ...values,
        expirationDate: values.expirationDate.format("YYYY-MM-DD"),
        type: couponType,
        discountType: discountType,
        trialDurationDays:
          couponType === "Coupon" && !scopesEventProduct ? values.trialDurationDays : null,
        discountValue: values.discountValue,
        // null/empty == global coupon (applies to every product)
        applicableProducts: values.applicableProducts || [],
        // null/empty == no package restriction (applies to every Digital Card plan).
        // Only meaningful when "digital-card" is one of the applicable products.
        applicablePlanIds: (values.applicableProducts || []).includes("digital-card")
          ? values.applicablePlanIds || []
          : [],
        // Combined Wedding + Event package SKU list. A SKU already identifies which
        // product type it belongs to, so both selectors write into this one field.
        // null/empty == no package restriction (applies to every package of whichever
        // wedding/event product type is selected above).
        applicableEventPackageSkus: [
          ...((values.applicableProducts || []).includes("wedding") ? values.weddingPackageSkus || [] : []),
          ...((values.applicableProducts || []).includes("event") ? values.eventPackageSkus || [] : []),
        ],
        // null/empty == unlimited redemptions per user (only the global Max Uses applies)
        maxUsesPerUser: values.maxUsesPerUser ?? null,
      };
      delete formData.weddingPackageSkus;
      delete formData.eventPackageSkus;
      if (!isEdit) {
        const response = await axios.post(`${BASE_URL}/Coupon`, formData);
        if (response.status === 201) {
          message.success("Coupon created successfully");
          navigate("/coupons");
        }
      } else {
        const response = await axios.put(`${BASE_URL}/Coupon/${id}`, formData);
        if (response.status === 200) {
          message.success("Coupon updated successfully");
          navigate("/coupons");
        }
      }
    } catch (error) {
      handleSaveError(error);
      setLoading(false);
    }
  };

  // Maps a server rejection to the form field it is about, so the user sees the message
  // right under that input and the page scrolls to / focuses it.
  const packageFieldName = showWeddingPackageSelector ? "weddingPackageSkus" : "eventPackageSkus";
  const serverMessageRules = [
    { test: /already exists|coupon code/i, field: "code" },
    { test: /expiration date/i, field: "expirationDate" },
    { test: /applicable package/i, field: packageFieldName },
    { test: /trial duration/i, field: "trialDurationDays" },
    { test: /discount type/i, field: "discountType" },
    { test: /discount value|percentage discount/i, field: "discountValue" },
  ];

  const showFieldErrors = (fieldErrors) => {
    form.setFields(fieldErrors.map(({ name, errors }) => ({ name, errors })));
    form.scrollToField(fieldErrors[0].name, { behavior: "smooth", block: "center" });
    // Let the scroll start, then put the cursor in the offending input.
    setTimeout(() => form.getFieldInstance?.(fieldErrors[0].name)?.focus?.(), 0);
  };

  const handleSaveError = (error) => {
    const data = error?.response?.data;

    // ASP.NET model validation: { errors: { FieldName: ["msg", ...] } }
    if (data?.errors && typeof data.errors === "object") {
      const known = Object.keys(form.getFieldsValue(true));
      const fieldErrors = Object.entries(data.errors)
        .map(([key, msgs]) => {
          const name = known.find((k) => k.toLowerCase() === key.toLowerCase());
          return name && { name, errors: [].concat(msgs) };
        })
        .filter(Boolean);
      if (fieldErrors.length > 0) {
        showFieldErrors(fieldErrors);
        message.error(fieldErrors[0].errors[0]);
        return;
      }
    }

    const serverMessage = data?.message || (typeof data === "string" ? data : null);
    if (serverMessage) {
      const match = serverMessageRules.find((rule) => rule.test.test(serverMessage));
      if (match) {
        showFieldErrors([{ name: match.field, errors: [serverMessage] }]);
      }
      message.error(serverMessage);
      return;
    }
    message.error(error?.message || "Failed to save coupon");
  };

  // A trial coupon scoped to Social Gathering/Event must name at least one package
  // (mirrors the backend rule), so flag the package selector instead of failing on submit.
  const trialPackageRule = {
    validator: () => {
      if (couponType !== "Coupon" || !scopesEventProduct) return Promise.resolve();
      const selected = [
        ...(showWeddingPackageSelector ? form.getFieldValue("weddingPackageSkus") || [] : []),
        ...(showEventPackageSelector ? form.getFieldValue("eventPackageSkus") || [] : []),
      ];
      return selected.length > 0
        ? Promise.resolve()
        : Promise.reject(new Error("Select at least one package for a trial coupon."));
    },
  };

  const generateCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    form.setFieldValue("code", result);
  };

  return (
    <Layout className="min-h-full bg-transparent">
      <div className="flex py-8 items-center max-w-7xl px-4 sm:px-6 lg:px-8">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/coupons")}
          className="mr-4"
        >
          Back
        </Button>
        <h1 className="text-2xl font-bold tracking-tight text-white">
          {isEdit ? "Edit Coupon" : "Create New Coupon"}
        </h1>
      </div>
      <Content className="rounded-lg px-6 py-6 shadow bg-white sm:px-8">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          scrollToFirstError={{ behavior: "smooth", block: "center" }}
          initialValues={{
            type: "Coupon",
            discountType: "percentage",
            isActive: true,
            maxUses: 100,
            maxUsesPerUser: 1,
            trialDurationDays: 60,
          }}
        >
          <Row gutter={20}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Coupon Type"
                name="type"
                rules={[
                  { required: true, message: "Please select coupon type" },
                ]}
              >
                <Select
                  value={couponType}
                  onChange={setCouponType}
                  placeholder={t("coupons.selectCouponType")}
                >
                  {couponTypeOptions.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Coupon Code"
                name="code"
                rules={[
                  { required: true, message: "Please enter coupon code" },
                  { min: 3, message: "Code must be at least 3 characters" },
                  { max: 20, message: "Code must not exceed 20 characters" },
                ]}
              >
                <Input
                  placeholder={t("coupons.enterCouponCode")}
                  suffix={
                    <Button type="link" size="small" onClick={generateCode}>
                      Generate
                    </Button>
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="Status" name="isActive" valuePropName="checked">
                <Switch 
                  checkedChildren="Active" 
                  unCheckedChildren="Inactive"
                  className="mt-2"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Coupon Name"
                name="name"
                rules={[
                  { required: true, message: "Please enter coupon name" },
                ]}
              >
                <Input placeholder={t("coupons.enterCouponName")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Description" name="description">
                <Input placeholder={t("coupons.enterCouponDescription")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Applicable Products"
                name="applicableProducts"
                extra="Leave empty to apply to all products (global coupon)."
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="All products (global)"
                  options={[
                    { value: "digital-card", label: "Digital Card" },
                    { value: "wedding", label: "Social Gathering" },
                    { value: "event", label: "Event" },
                  ]}
                />
              </Form.Item>
            </Col>
            {showPlanSelector && (
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Digital Card Packages"
                  name="applicablePlanIds"
                  extra="Leave empty to apply to all Digital Card plans."
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All Digital Card plans"
                    options={plans.map((plan) => ({
                      value: plan.id,
                      label: plan.name,
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
            {showWeddingPackageSelector && (
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Social Gatherings Packages"
                  name="weddingPackageSkus"
                  rules={[trialPackageRule]}
                  extra="Select packages to restrict this coupon. Leave empty to apply to all Social Gatherings packages."
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All Social Gatherings packages"
                    options={weddingPackages.map((pkg) => ({
                      value: pkg.sku,
                      label: pkg.isActive ? pkg.nameEn : `${pkg.nameEn} (Inactive)`,
                      disabled: !pkg.isActive,
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
            {showEventPackageSelector && (
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Event Packages"
                  name="eventPackageSkus"
                  rules={[trialPackageRule]}
                  extra="Select packages to restrict this coupon. Leave empty to apply to all Event packages."
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All Event packages"
                    options={eventOnlyPackages.map((pkg) => ({
                      value: pkg.sku,
                      label: pkg.isActive ? pkg.nameEn : `${pkg.nameEn} (Inactive)`,
                      disabled: !pkg.isActive,
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={12}>
              <Form.Item
                label="Max Uses Per User"
                name="maxUsesPerUser"
                extra="Leave empty for unlimited redemptions per user."
                rules={[
                  { type: "number", min: 1, message: "Must be at least 1" },
                ]}
              >
                <InputNumber
                  min={1}
                  placeholder="Unlimited"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Dynamic Settings Section */}
          {couponType === "Coupon" ? (
            <Row gutter={20}>
              {scopesEventProduct ? (
                <Col xs={24} sm={8}>
                  <Typography.Text type="secondary" style={{ display: "block", marginTop: 30 }}>
                    This trial coupon makes the selected Social Gatherings/Event package(s) above
                    completely free — no trial duration. The customer receives that package's
                    full configuration (Max Events, invitation/registration/VIP limits, and
                    features) exactly as purchased.
                  </Typography.Text>
                </Col>
              ) : (
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Trial Duration (Days)"
                    name="trialDurationDays"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Coupon duration",
                      },
                      {
                        type: "number",
                        min: 1,
                        message: "Must be at least 1 day",
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={365}
                      placeholder={t("coupons.enterDuration")}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Maximum Uses"
                  name="maxUses"
                  rules={[
                    { required: true, message: "Please enter maximum uses" },
                    { type: "number", min: 1, message: "Must be at least 1" },
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={10000}
                    placeholder={t("coupons.enterMaxUses")}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Expiration Date"
                  name="expirationDate"
                  rules={[
                    {
                      required: true,
                      message: "Please select expiration date",
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="Select date"
                    disabledDate={(current) =>
                      current && current < dayjs().endOf("day")
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row gutter={20}>
              <Col xs={24} sm={6}>
                <Form.Item
                  label="Discount Type"
                  name="discountType"
                  rules={[
                    {
                      required: true,
                      message: "Please select Discount type",
                    },
                  ]}
                >
                  <Select
                    value={discountType}
                    onChange={setDiscountType}
                    placeholder="Select type"
                  >
                    {discountTypeOptions.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item
                  label={`Value ${
                    discountType === "Percentage" ? "(%)" : "(SAR)"
                  }`}
                  name="discountValue"
                  rules={[
                    {
                      required: true,
                      message: "Please enter value",
                    },
                    {
                      type: "number",
                      min: 0.01,
                      message: "Must be greater than 0",
                    },
                    discountType === "Percentage"
                      ? {
                          type: "number",
                          max: 100,
                          message: "Cannot exceed 100%",
                        }
                      : {
                          type: "number",
                          max: 10000,
                          message: "Cannot exceed 10,000 SAR",
                        },
                  ]}
                >
                  <InputNumber
                    min={0.01}
                    max={discountType === "Percentage" ? 100 : 10000}
                    step={discountType === "Percentage" ? 1 : 0.01}
                    placeholder="Enter value"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item
                  label="Maximum Uses"
                  name="maxUses"
                  rules={[
                    { required: true, message: "Please enter maximum uses" },
                    { type: "number", min: 1, message: "Must be at least 1" },
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={10000}
                    placeholder="Enter max uses"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item
                  label="Expiration Date"
                  name="expirationDate"
                  rules={[
                    {
                      required: true,
                      message: "Please select expiration date",
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="Select date"
                    disabledDate={(current) =>
                      current && current < dayjs().endOf("day")
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
            <Button onClick={() => navigate("/coupons")}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
            >
              {isEdit ? "Update Coupon" : "Create Coupon"}
            </Button>
          </div>
        </Form>
      </Content>
    </Layout>
  );
}

export default CouponForm;
